import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const response = await fetch(`${API_URL}/api/admin/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Store admin token
          // SECURITY WARNING: Using sessionStorage is vulnerable to XSS attacks
          // TODO: Migrate to httpOnly cookies - see /app/SECURITY_TODO.md
          sessionStorage.setItem('adminToken', data.token);
          sessionStorage.setItem('adminUser', JSON.stringify({
            username: data.username,
            role: data.role
          }));
          
          // Navigate to admin dashboard
          navigate('/admin');
        } else {
          setErrors({ submit: 'Invalid username or password' });
        }
      } catch (error) {

        setErrors({ submit: 'Unable to login. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="admin-login-page" style={{ minHeight: '100vh', backgroundColor: '#0f172a' }}>
      <nav style={{ 
        padding: '1rem 2rem', 
        borderBottom: '1px solid #1e293b',
        backgroundColor: '#1e293b'
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Shield size={24} color="#f59e0b" />
          <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '1.25rem' }}>ReturnPal Admin</span>
        </div>
      </nav>

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '3rem 1rem',
        minHeight: 'calc(100vh - 80px)'
      }}>
        <Card style={{ maxWidth: '450px', width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155' }}>
          <CardHeader style={{ textAlign: 'center' }}>
            <div style={{ 
              margin: '0 auto 1rem', 
              width: '64px', 
              height: '64px', 
              borderRadius: '50%', 
              backgroundColor: '#f59e0b', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Shield size={32} color="white" />
            </div>
            <CardTitle style={{ fontSize: '1.875rem', marginBottom: '0.5rem', color: '#f1f5f9' }}>
              Admin Access
            </CardTitle>
            <CardDescription style={{ color: '#94a3b8' }}>
              Authorized personnel only
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="form-group">
                <Label htmlFor="username" style={{ color: '#cbd5e1' }}>
                  <Mail size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Username
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="admin"
                  value={formData.username}
                  onChange={handleChange}
                  className={errors.username ? 'input-error' : ''}
                  style={{ 
                    marginTop: '0.5rem', 
                    backgroundColor: '#0f172a', 
                    border: '1px solid #334155', 
                    color: '#f1f5f9' 
                  }}
                />
                {errors.username && <span className="error-message" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.username}</span>}
              </div>

              <div className="form-group">
                <Label htmlFor="password" style={{ color: '#cbd5e1' }}>
                  <Lock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? 'input-error' : ''}
                  style={{ 
                    marginTop: '0.5rem', 
                    backgroundColor: '#0f172a', 
                    border: '1px solid #334155', 
                    color: '#f1f5f9' 
                  }}
                />
                {errors.password && <span className="error-message" style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.password}</span>}
              </div>

              {errors.submit && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#7f1d1d', 
                  border: '1px solid #991b1b', 
                  borderRadius: '0.375rem',
                  color: '#fecaca',
                  fontSize: '0.875rem'
                }}>
                  {errors.submit}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  backgroundColor: '#f59e0b',
                  color: 'white'
                }}
              >
                {isSubmitting ? 'Logging in...' : 'Access Dashboard'}
              </Button>

              <div style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                <button
                  type="button"
                  onClick={() => navigate('/admin/forgot-password')}
                  data-testid="forgot-password-link"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fbbf24',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                    fontSize: '0.875rem',
                  }}
                >
                  Forgot username or password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;
